import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { AxiosResponse } from 'axios'
import { toast } from 'react-toastify'
import {
  ICreateDeliveryTicketRequest,
  IDeliveryTicketResponse,
  IMaterialReceiptSiteConfigResponse,
  IPutMaterialReceiptSiteConfigRequest,
} from 'types/api/operationsAimApi.interface'
import { DeliveryTicketStatus } from 'types/operationsEnums.interface'
import {
  IMaterialAuditEntry,
  IMaterialDelivery,
  IMaterialReceiptConfig,
  IMaterialReceiptSlice,
} from 'types/materialReceipt.interface'
import { baseUrl } from 'utils/axios'
import { handleErrors } from 'utils/helpers'
import { MATERIAL_BASIS_TO_UI } from 'pages/Operations/Shared/opsEnumMaps'
const mapTicketToDelivery = (row: IDeliveryTicketResponse): IMaterialDelivery => ({
  id: String(row.id),
  plate: null,
  supplier: row.supplierName ?? '—',
  source: row.materialType,
  preGrade: null,
  gross: row.grossWeight,
  tare: row.tareWeight,
  moisture: row.moisturePct,
  grade: row.grade,
  driver: null,
  waybill: row.ticketNo,
  fleetLinked: false,
  state: row.status === DeliveryTicketStatus.Finalized ? 'FINALIZED' : 'OPEN',
  date: row.createdAt,
  flagged: row.hasDiscrepancy,
  corrections: [],
  prov: {},
})

const initialState: IMaterialReceiptSlice & { isLoading: boolean } = {
  isLoading: false,
  deliveries: [],
  auditLog: [],
  config: { basis: 'DRY', threshold: 0 },
}

export const getDeliveryTickets = createAsyncThunk(
  'materialReceipt/getDeliveryTickets',
  async (params: any, thunkAPI) => {
    try {
      const resp: AxiosResponse<IDeliveryTicketResponse[]> = await baseUrl.get(
        'aim-operations/material-receipt/ticket',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const createDeliveryTicket = createAsyncThunk(
  'materialReceipt/createDeliveryTicket',
  async (body: ICreateDeliveryTicketRequest, thunkAPI) => {
    try {
      const resp: AxiosResponse<IDeliveryTicketResponse> = await baseUrl.post(
        'aim-operations/material-receipt/ticket',
        body,
      )
      toast.success('Delivery ticket created')
      await thunkAPI.dispatch(getDeliveryTickets({}))
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const patchDeliveryTicketStatus = createAsyncThunk(
  'materialReceipt/patchDeliveryTicketStatus',
  async ({ id, status }: { id: number; status: DeliveryTicketStatus }, thunkAPI) => {
    try {
      await baseUrl.patch(`aim-operations/material-receipt/ticket/${id}/status`, { status })
      await thunkAPI.dispatch(getDeliveryTickets({}))
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const addDeliveryAssay = createAsyncThunk(
  'materialReceipt/addDeliveryAssay',
  async ({ id, grade, moisturePct }: { id: number; grade: number; moisturePct?: number }, thunkAPI) => {
    try {
      await baseUrl.post(`aim-operations/material-receipt/ticket/${id}/assay`, { grade, moisturePct })
      toast.success('Assay recorded')
      await thunkAPI.dispatch(getDeliveryTickets({}))
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const finalizeDeliveryTicket = createAsyncThunk(
  'materialReceipt/finalizeDeliveryTicket',
  async (id: number, thunkAPI) => {
    try {
      await baseUrl.post(`aim-operations/material-receipt/ticket/${id}/finalize`)
      toast.success('Ticket finalized')
      await thunkAPI.dispatch(getDeliveryTickets({}))
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const reopenDeliveryTicket = createAsyncThunk(
  'materialReceipt/reopenDeliveryTicket',
  async ({ id, reason }: { id: number; reason: string }, thunkAPI) => {
    try {
      await baseUrl.post(`aim-operations/material-receipt/ticket/${id}/reopen`, { reason })
      toast.success('Ticket reopened')
      await thunkAPI.dispatch(getDeliveryTickets({}))
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getMaterialReceiptSiteConfig = createAsyncThunk(
  'materialReceipt/getMaterialReceiptSiteConfig',
  async (_, thunkAPI) => {
    try {
      const resp: AxiosResponse<IMaterialReceiptSiteConfigResponse> = await baseUrl.get(
        'aim-operations/material-receipt/site-config',
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const putMaterialReceiptSiteConfig = createAsyncThunk(
  'materialReceipt/putMaterialReceiptSiteConfig',
  async (body: IPutMaterialReceiptSiteConfigRequest, thunkAPI) => {
    try {
      const resp: AxiosResponse<IMaterialReceiptSiteConfigResponse> = await baseUrl.put(
        'aim-operations/material-receipt/site-config',
        body,
      )
      toast.success('Site config saved')
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

const mapConfig = (row: IMaterialReceiptSiteConfigResponse): IMaterialReceiptConfig => ({
  basis: MATERIAL_BASIS_TO_UI[row.basis] ?? 'DRY',
  threshold: row.discrepancyThresholdPct,
})

const materialReceiptSlice = createSlice({
  name: 'materialReceipt',
  initialState,
  reducers: {
    patchDelivery: (state, action: { payload: { id: string; fields: Partial<IMaterialDelivery> } }) => {
      const { id, fields } = action.payload
      state.deliveries = state.deliveries.map((d) => (d.id === id ? { ...d, ...fields } : d))
    },
    setBasis: (state, action: { payload: IMaterialReceiptConfig['basis'] }) => {
      state.config.basis = action.payload
    },
    setThreshold: (state, action: { payload: number }) => {
      state.config.threshold = action.payload
    },
    logAudit: (state, action: PayloadAction<Omit<IMaterialAuditEntry, 'id' | 'at'> & { at?: string }>) => {
      const { at, ...rest } = action.payload
      state.auditLog = [
        { id: state.auditLog.length + 1, at: at || new Date().toISOString(), ...rest },
        ...state.auditLog,
      ]
    },
  },
  extraReducers: {
    [getDeliveryTickets.pending.type]: (state) => {
      state.isLoading = true
    },
    [getDeliveryTickets.fulfilled.type]: (state, { payload }: { payload: IDeliveryTicketResponse[] }) => {
      state.isLoading = false
      state.deliveries = (payload || []).map(mapTicketToDelivery)
    },
    [getDeliveryTickets.rejected.type]: (state, action) => {
      state.isLoading = false
      handleErrors(action.payload)
    },
    [getMaterialReceiptSiteConfig.fulfilled.type]: (
      state,
      { payload }: { payload: IMaterialReceiptSiteConfigResponse },
    ) => {
      state.config = mapConfig(payload)
    },
    [putMaterialReceiptSiteConfig.fulfilled.type]: (
      state,
      { payload }: { payload: IMaterialReceiptSiteConfigResponse },
    ) => {
      state.config = mapConfig(payload)
    },
    [createDeliveryTicket.rejected.type]: (state, action) => handleErrors(action.payload),
    [patchDeliveryTicketStatus.rejected.type]: (state, action) => handleErrors(action.payload),
    [addDeliveryAssay.rejected.type]: (state, action) => handleErrors(action.payload),
    [finalizeDeliveryTicket.rejected.type]: (state, action) => handleErrors(action.payload),
    [reopenDeliveryTicket.rejected.type]: (state, action) => handleErrors(action.payload),
  },
})

export const { patchDelivery, setBasis, setThreshold, logAudit } = materialReceiptSlice.actions
export default materialReceiptSlice.reducer
